import { CheckCircle2, Filter, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { usePublishComments, useUpdateFinding } from '../../hooks/queries';
import { readPreferences } from '../../hooks/usePreferences';
import { applyFindingFilters, defaultFilters } from '../../lib/findingFilters';
import type { FindingFilterState } from '../../lib/findingFilters';
import type { Finding, FindingStatus, Review, Severity } from '../../types/api';
import { SEVERITIES } from '../../types/api';
import { Button } from '../ui/Button';
import { EmptyState, Notice } from '../ui/Feedback';
import { useToast } from '../ui/Toast';
import { DismissDialog } from './DismissDialog';
import { FindingCard } from './FindingCard';
import { FindingFilters } from './FindingFilters';
import { PublishDialog } from './PublishDialog';

export function FindingsTab({ review, findings, canPublish }: { review: Review; findings: Finding[]; canPublish: boolean }) {
  const [filters, setFilters] = useState<FindingFilterState>(() => defaultFilters(readPreferences().defaultSeverities));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dismissing, setDismissing] = useState<Finding | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const update = useUpdateFinding(review.id);
  const publish = usePublishComments(review.id);
  const toast = useToast();

  const visible = useMemo(() => applyFindingFilters(findings, filters), [findings, filters]);
  const files = useMemo(() => [...new Set(findings.map((f) => f.filePath))].sort(), [findings]);
  const severityCounts = useMemo(() => {
    const c = Object.fromEntries(SEVERITIES.map((s) => [s, 0])) as Record<Severity, number>;
    for (const f of findings) c[f.severity]++;
    return c;
  }, [findings]);

  const setStatus = (f: Finding, status: FindingStatus, dismissReason?: string) =>
    update.mutate(
      { id: f.id, status, dismissReason },
      {
        onSuccess: () => {
          if (status === 'dismissed') {
            setSelected((s) => {
              const n = new Set(s);
              n.delete(f.id);
              return n;
            });
            setDismissing(null);
          }
        },
        onError: (e) => toast(e.message, 'error'),
      },
    );

  if (findings.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="h-6 w-6 text-ok" />}
        title="No potential issues found"
        description={
          review.source === 'demo'
            ? 'This sample review returned no findings.'
            : 'The reviewer did not report any actionable issues in the analyzed changes. This is not a guarantee that the code is defect-free.'
        }
      />
    );
  }

  const publishable = canPublish || review.source === 'demo';
  const selectedIds = [...selected];

  return (
    <div className="space-y-4">
      <Notice tone="info">
        These are <strong className="font-medium text-ink">potential issues</strong> generated by{' '}
        {review.source === 'demo' ? 'the demo reviewer (sample data)' : 'an AI model'}. They are not verified defects — review each one
        before acting.
      </Notice>

      <div className="card p-4">
        <FindingFilters
          value={filters}
          onChange={setFilters}
          files={files}
          severityCounts={severityCounts}
          onReset={() => setFilters(defaultFilters([...SEVERITIES]))}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted" aria-live="polite">
          Showing {visible.length} of {findings.length} findings
          {selected.size > 0 && ` · ${selected.size} selected`}
        </p>
        {publishable && (
          <Button
            variant="primary"
            size="sm"
            disabled={selected.size === 0}
            onClick={() => setPublishOpen(true)}
            icon={<Send className="h-3.5 w-3.5" />}
            title={selected.size === 0 ? 'Select findings to publish' : undefined}
          >
            Publish to GitHub…
          </Button>
        )}
      </div>
      {!publishable && (
        <p className="text-xs text-faint">Publishing to GitHub is unavailable: the server has no GITHUB_TOKEN configured.</p>
      )}

      {visible.length === 0 ? (
        <div className="card">
          <EmptyState icon={<Filter className="h-5 w-5" />} title="No findings match these filters" />
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((f, i) => (
            <FindingCard
              key={f.id}
              finding={f}
              defaultOpen={i === 0}
              selectable={publishable}
              selected={selected.has(f.id)}
              onSelect={(on) =>
                setSelected((s) => {
                  const n = new Set(s);
                  if (on) n.add(f.id);
                  else n.delete(f.id);
                  return n;
                })
              }
              onStatus={(status) => setStatus(f, status)}
              onDismiss={() => setDismissing(f)}
              busy={update.isPending && update.variables?.id === f.id}
            />
          ))}
        </div>
      )}

      <DismissDialog
        finding={dismissing}
        onClose={() => setDismissing(null)}
        onConfirm={(reason) => dismissing && setStatus(dismissing, 'dismissed', reason || undefined)}
        busy={update.isPending}
      />

      <PublishDialog
        open={publishOpen}
        review={review}
        findingIds={selectedIds}
        onClose={() => {
          setPublishOpen(false);
          publish.reset();
        }}
        publishing={publish.isPending}
        publishError={publish.error}
        onPublish={() =>
          publish.mutateAsync(selectedIds).then((r) => {
            setPublishOpen(false);
            setSelected(new Set());
            toast(
              r.simulated
                ? `Demo: ${r.publishedCount} comment(s) simulated — nothing was posted.`
                : `Published ${r.publishedCount} finding(s) to GitHub${r.foldedCount ? ` (${r.foldedCount} moved to the review body due to outdated lines)` : ''}.`,
              'success',
            );
            return r;
          })
        }
      />
    </div>
  );
}
